import { useEffect, useState } from 'react';

import { getItemWithExpiry } from '@netfront/common-library';
import { IUser, useAuthentication, useGetLoggedUser } from '@netfront/gelada-identity-library';

import UserContext from './UserContext';
import { UserContextProps } from './UserContext.interfaces';


export function UserProvider({ children }: UserContextProps) {
  const { isAuthenticated } = useAuthentication();

  const [profileImageUrl, setProfileImageUrl] = useState<string | null>();
  const [fullName, setFullName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmailName] = useState('');
  const [user, setUser] = useState<IUser>();
  const [userId, setUserId] = useState<number>();
  const [storedUser, setStoredUser] = useState<IUser>();
  const [projectExpiryStored, setProjectExpiryStored] = useState<number>();

  const { handleGetLoggedUser } = useGetLoggedUser({
    onCompleted: ({ user: returnedUser }) => {
      setStoredUser(returnedUser);
    },
    product: 'QUICKCODES',
  });

  useEffect(() => {
    const locallyStoredUser = localStorage.getItem('storedUser');

    if (locallyStoredUser) {
      const parsedUserWithExpiry = JSON.parse(locallyStoredUser);
      const parsedUser = JSON.parse(String(parsedUserWithExpiry.value ?? ''));
      setProjectExpiryStored(Number(parsedUserWithExpiry.expiry));
      const { profileImageUrl: image, firstName: returnedFirstName, lastName: returnedLastName, id, email: returnedEmail } = parsedUser;
      setProfileImageUrl(decodeURIComponent(String(image)));
      setFullName(`${String(returnedFirstName)} ${String(returnedLastName)}`);
      setFirstName(String(returnedFirstName));
      setLastName(String(returnedLastName));
      setEmailName(String(returnedEmail));
      setUserId(Number(id));
      setUser(parsedUser as IUser);
    }
  }, []);

  useEffect(() => {
    if (storedUser) {
      const { pictures, firstName: returnedFirstName, lastName: returnedLastName, id, email: returnedEmail = '' } = storedUser;

      const { originalPicture, profilePicture } = pictures ?? {};

      const image = !profilePicture?.presignedUrl ? originalPicture?.presignedUrl ?? '' : profilePicture.presignedUrl;

      localStorage.setItem(
        'storedUser',
        getItemWithExpiry(
          JSON.stringify({
            id,
            firstName: returnedFirstName,
            lastName: returnedLastName,
            email: returnedEmail,
            profileImageUrl: encodeURIComponent(image),
          }),
          {
            currentTimeValueInMilliseconds: new Date().getTime(),
            expiryTime: {
              unit: 'days',
              value: 1,
            },
          },
        ),
      );

      setProfileImageUrl(image);
      setFullName(`${returnedFirstName} ${returnedLastName}`);
      setFirstName(returnedFirstName);
      setLastName(returnedLastName);
      setEmailName(returnedEmail);
      setUserId(Number(id));
      setUser(storedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]);

  useEffect(() => {
    if (!projectExpiryStored) {
      return;
    }

    if (new Date().getTime() < projectExpiryStored) {
      return;
    }

    if (!isAuthenticated) return;

    void handleGetLoggedUser({ shouldIncludeUserPictures: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectExpiryStored]);

  const getUser = () => {
    return user;
  };

  return (
    <UserContext.Provider
      value={{
        userId,
        firstName,
        lastName,
        email,
        profileImageUrl,
        fullName,
        setProfileImageUrl,
        setStoredUser,
        user,
        getUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
